import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloudOff, AlertTriangle, X } from 'lucide-react';
import { backupAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Ambient badge in the admin topbar that surfaces a Synology backup
 * problem before the doctor stumbles onto it.
 *
 *   • Renders NOTHING when everything is healthy (alert_level = 'ok') —
 *     no visual noise on the happy path.
 *   • Amber pill after 1-2 consecutive failed uploads (level 'warning').
 *   • Red pill after ≥3 consecutive failures (level 'critical').
 *   • Only visible for superadmins (patients / doctors don't need it).
 *   • Clicking it deep-links to the Synology diagnostics panel inside
 *     the settings page.
 *
 * Polls every 5 minutes and on window focus so a fresh nightly failure
 * shows up the next morning without a page reload.
 */
export default function SynologyAlertBadge() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [alertLevel, setAlertLevel] = useState('ok');
  const [consecutive, setConsecutive] = useState(0);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await backupAPI.synologyDiagnostics();
        if (cancelled) return;
        setAlertLevel(data?.alert_level || 'ok');
        setConsecutive(data?.consecutive_failures || 0);
      } catch (_) {
        // Silent — we don't want a network blip to flag a fake alert.
      }
    };

    load();
    const interval = setInterval(load, 5 * 60_000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;
  if (alertLevel === 'ok') return null;

  const isCritical = alertLevel === 'critical';
  const Icon = isCritical ? CloudOff : AlertTriangle;
  const colorCls = isCritical
    ? 'bg-rose-100 text-rose-700 border-rose-300 hover:bg-rose-200'
    : 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200';

  return (
    <div className={`inline-flex items-center gap-0 rounded-full border transition-colors ${colorCls}`}>
      <button
        type="button"
        onClick={() => navigate('/admin/settings#synology')}
        title={
          isCritical
            ? `Backup Synology falló ${consecutive} veces seguidas — abre diagnóstico`
            : `${consecutive} fallo${consecutive === 1 ? '' : 's'} de backup Synology desde el último éxito`
        }
        className="flex items-center gap-1.5 pl-2.5 pr-2 py-1 text-xs font-medium"
        data-testid={`synology-alert-badge-${alertLevel}`}
      >
        <Icon size={13} />
        <span className="hidden sm:inline">
          {isCritical ? 'Backup NAS caído' : 'Backup NAS con fallos'}
        </span>
        <span className="font-mono">×{consecutive}</span>
      </button>
      <button
        type="button"
        onClick={async (e) => {
          e.stopPropagation();
          try {
            await backupAPI.ackSynologyAlert();
            setAlertLevel('ok');
          } catch (_) {
            // Silent — the next poll will re-evaluate anyway.
          }
        }}
        title="Marcar como visto (el aviso reaparecerá si hay un nuevo fallo)"
        aria-label="Descartar alerta de backup"
        className="p-1 pr-2 rounded-r-full hover:opacity-70"
        data-testid="synology-alert-dismiss"
      >
        <X size={11} />
      </button>
    </div>
  );
}
